/**
 * Timeline Header helpers
 * Span computation and label formatters extracted from BoardV3TimelineHeader
 */

import { DAYS_PER_WEEK, getMonthNamesShort } from '../../utils/boardHelpers';
import { businessDayIndexToDate, getMonthAndYear } from '../../../lib/dateUtils';

// ============================================
// Types
// ============================================

export interface VisibleWeek {
  kw: number;
  year: number;
  startDay: number;
  weekIndex: number;
}

export interface MonthSpan {
  month: number;
  year: number;
  label: string;
  startDay: number;
  endDay: number;
}

export interface YearSpan {
  year: number;
  startDay: number;
  endDay: number;
}

// ============================================
// Generic span computation
// ============================================

interface SpanAccumulator<T> {
  getKey: (dateStr: string) => string;
  createSpan: (dateStr: string, startDay: number) => T;
  updateEnd: (span: T, endDay: number) => void;
}

function computeSpans<T>(
  visibleWeeks: VisibleWeek[],
  boardStartDate: string | undefined,
  acc: SpanAccumulator<T>,
): T[] {
  if (visibleWeeks.length === 0) return [];

  const spans: T[] = [];
  let currentSpan: T | null = null;
  let currentKey: string | null = null;

  for (const week of visibleWeeks) {
    const dateStr = businessDayIndexToDate(week.startDay, boardStartDate);
    const key = acc.getKey(dateStr);

    if (key !== currentKey) {
      if (currentSpan) {
        acc.updateEnd(currentSpan, week.startDay);
        spans.push(currentSpan);
      }
      currentSpan = acc.createSpan(dateStr, week.startDay);
      currentKey = key;
    } else if (currentSpan) {
      acc.updateEnd(currentSpan, week.startDay + DAYS_PER_WEEK);
    }
  }

  if (currentSpan) {
    spans.push(currentSpan);
  }

  return spans;
}

export function computeMonthSpans(visibleWeeks: VisibleWeek[], boardStartDate?: string, locale = 'de'): MonthSpan[] {
  const monthNames = getMonthNamesShort(locale);
  return computeSpans(visibleWeeks, boardStartDate, {
    getKey: (dateStr) => {
      const { month, year } = getMonthAndYear(dateStr);
      return `${year}-${month}`;
    },
    createSpan: (dateStr, startDay) => {
      const { month, year } = getMonthAndYear(dateStr);
      return { month, year, label: monthNames[month], startDay, endDay: startDay + DAYS_PER_WEEK };
    },
    updateEnd: (span, endDay) => { span.endDay = endDay; },
  });
}

export function computeYearSpans(visibleWeeks: VisibleWeek[], boardStartDate?: string): YearSpan[] {
  return computeSpans(visibleWeeks, boardStartDate, {
    getKey: (dateStr) => {
      const { year } = getMonthAndYear(dateStr);
      return String(year);
    },
    createSpan: (dateStr, startDay) => {
      const { year } = getMonthAndYear(dateStr);
      return { year, startDay, endDay: startDay + DAYS_PER_WEEK };
    },
    updateEnd: (span, endDay) => { span.endDay = endDay; },
  });
}

// ============================================
// Label formatters
// ============================================

export function formatKwLabel(kw: number, year: number, weekWidth: number): string {
  if (weekWidth >= 80) return `KW ${kw}/${String(year).slice(-2)}`;
  if (weekWidth >= 50) return `KW ${kw}`;
  if (weekWidth >= 25) return `${kw}`;
  return '';
}

export function formatMonthLabel(month: number, year: number, spanWidth: number, locale = 'de'): string {
  const monthNames = getMonthNamesShort(locale);
  if (spanWidth >= 60) return `${monthNames[month]} ${String(year).slice(-2)}`;
  if (spanWidth >= 30) return monthNames[month];
  return monthNames[month][0];
}
